import React, { useState } from 'react';
import { 
  Brain, ShieldCheck, Heart, Sparkles, Activity, Layers, 
  ArrowRight, CheckCircle2, Stethoscope, Compass, Cpu, RefreshCw
} from 'lucide-react';
import { AbilityBrandLogo, ClarvosCTAButton, ClarvosPillBadge } from './ClarvosBrand';

interface MethodologyPageProps {
  onExploreDemo: (tab: any) => void;
  onRequestDemo: () => void;
}

export const MethodologyPage: React.FC<MethodologyPageProps> = ({ onExploreDemo, onRequestDemo }) => {
  const [activePrinciple, setActivePrinciple] = useState(0);

  const principles = [
    {
      id: '01',
      title: 'Evidence-Based Cognitive & Clinical Modeling',
      desc: 'Care decisions should never rely on fragmented records or subjective guesswork. AbilityOS grounds every AI recommendation in verified clinical taxonomies (ICF, DSM-5-TR, R-PTA).',
      tag: 'Clinical Grounding',
      stat: '99.4%',
      statLabel: 'Diagnostic Cross-Verification',
    },
    {
      id: '02',
      title: 'Adaptive Multi-Sensory Accessibility Loops',
      desc: 'Every neurodivergent and motor-impaired individual communicates differently. Our agentic feedback loop dynamically adjusts eye-gaze dwell, audio pitch, high-contrast, and AAC tile densities in real-time.',
      tag: 'Dynamic Adaptation',
      stat: '40ms',
      statLabel: 'Real-time Dwell Adaptation',
    },
    {
      id: '03',
      title: 'Human-in-the-Loop Clinical Governance',
      desc: 'AI models never execute unattended therapy or medication changes. Clinicians, special educators, and therapists maintain full editorial oversight with one-click audit approval.',
      tag: 'Safety Guardrails',
      stat: '100%',
      statLabel: 'Audited Intervention Logs',
    },
    {
      id: '04',
      title: 'Longitudinal Telemetry & Outcome Prediction',
      desc: 'Continuous micro-signals from speech synthesis, AAC selection velocity, and motor dwell simulate progress trajectories to prevent developmental regression weeks before it manifests.',
      tag: 'Predictive Care',
      stat: '+3.4x',
      statLabel: 'Faster Milestones Achievement',
    },
  ];

  return (
    <div className="bg-clarvos-cream min-h-screen text-clarvos-black">
      {/* 1. Hero Section - Deep Dark Slate / Obsidian */}
      <section className="bg-clarvos-black text-white pt-28 pb-20 px-6 sm:px-12 rounded-b-[40px] relative overflow-hidden">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          
          {/* Orbital Solar Yellow Emblem with Orbiting Icons */}
          <div className="relative w-64 h-64 mx-auto mb-12 flex items-center justify-center">
            {/* Center Solar Yellow Circle */}
            <div className="w-44 h-44 rounded-full bg-clarvos-yellow text-clarvos-black flex items-center justify-center shadow-2xl relative z-10 transition-transform duration-700 hover:scale-105">
              <AbilityBrandLogo variant="dark" size="lg" className="flex-col !gap-1" />
            </div>

            {/* Orbiting Satellite 1 (Rocket / Empower) */}
            <div className="absolute -top-2 left-6 w-16 h-16 rounded-full bg-white text-clarvos-black flex items-center justify-center shadow-xl border-4 border-clarvos-black animate-bounce">
              <Cpu className="w-7 h-7 text-clarvos-black" />
            </div>

            {/* Orbiting Satellite 2 (Handshake / Trust) */}
            <div className="absolute top-12 -right-4 w-16 h-16 rounded-full bg-white text-clarvos-black flex items-center justify-center shadow-xl border-4 border-clarvos-black">
              <ShieldCheck className="w-7 h-7 text-emerald-600" />
            </div>

            {/* Orbiting Satellite 3 (Bar Chart / Telemetry) */}
            <div className="absolute -bottom-2 right-10 w-14 h-14 rounded-full bg-white text-clarvos-black flex items-center justify-center shadow-xl border-4 border-clarvos-black">
              <Activity className="w-6 h-6 text-clarvos-blue" />
            </div>
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight max-w-4xl mx-auto leading-[1.1] mb-6">
            Where smarter clinical decisions lead to human independence
          </h1>
          <p className="text-lg sm:text-xl text-clarvos-gray-300 max-w-2xl mx-auto leading-relaxed mb-10 font-normal">
            AbilityOS was engineered on one steadfast principle: that clinical interventions and assistive technology should be guided by real patient behavioral signals — not fragmented guesswork.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <ClarvosCTAButton
              text="Request Clinical Walkthrough"
              hoverText="Schedule Demo"
              onClick={onRequestDemo}
              variant="yellow"
              size="lg"
            />
            <ClarvosCTAButton
              text="Explore Interactive Modules"
              hoverText="Launch Platform"
              onClick={() => onExploreDemo('modules')}
              variant="dark"
              size="lg"
              className="border border-white/20"
            />
          </div>
        </div>
      </section>

      {/* 2. Principles Section */}
      <section className="py-24 px-6 sm:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <ClarvosPillBadge number="01" label="Core Philosophy" variant="dark" className="mb-4" />
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
              The Principles Behind AbilityOS
            </h2>
          </div>
          <p className="text-clarvos-gray-600 max-w-md text-base leading-relaxed">
            Every layer of our neural orchestration architecture adheres to rigorous healthcare ethics and bio-mechanical feedback loops.
          </p>
        </div>

        {/* 4 Interactive Principles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {principles.map((item, idx) => (
            <div
              key={item.id}
              onClick={() => setActivePrinciple(idx)}
              className={`p-8 sm:p-10 rounded-[32px] border transition-all duration-500 cursor-pointer ${
                activePrinciple === idx
                  ? 'bg-white border-clarvos-yellow shadow-xl ring-2 ring-clarvos-yellow/50 scale-[1.01]'
                  : 'bg-clarvos-sand/50 border-clarvos-gray-300/60 hover:bg-white hover:border-clarvos-gray-400'
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="text-3xl font-mono font-bold text-clarvos-black/30">
                  {item.id}
                </span>
                <span className="px-3.5 py-1.5 rounded-full bg-clarvos-cream border border-clarvos-gray-300 text-xs font-bold uppercase tracking-wider text-clarvos-black">
                  {item.tag}
                </span>
              </div>

              <h3 className="text-2xl font-bold mb-3 tracking-tight">{item.title}</h3>
              <p className="text-clarvos-gray-600 leading-relaxed mb-8 text-sm sm:text-base">
                {item.desc}
              </p>

              <div className="pt-6 border-t border-clarvos-gray-300/50 flex items-center justify-between">
                <div>
                  <div className="text-3xl font-black text-clarvos-black">{item.stat}</div>
                  <div className="text-xs text-clarvos-gray-600 font-medium">{item.statLabel}</div>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  activePrinciple === idx ? 'bg-clarvos-yellow text-clarvos-black' : 'bg-clarvos-gray-200 text-clarvos-gray-600'
                }`}>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. The Continuous Feedback Loop */}
      <section className="py-20 px-6 sm:px-12 bg-white border-y border-clarvos-gray-300">
        <div className="max-w-6xl mx-auto text-center">
          <ClarvosPillBadge label="Continuous Engine" variant="yellow" className="mb-6" />
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-6">
            The AbilityOS Clinical Framework: How Interventions Adapt
          </h2>
          <p className="text-clarvos-gray-600 text-lg max-w-2xl mx-auto mb-16">
            Each phase seamlessly feeds into the next — creating a continuous loop of insight, simulation, and measurable independence.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
            {[
              {
                step: '1. Ingest',
                desc: 'Collect multi-modal behavioral signals (gaze dwell, vocal acoustic pitch, AAC velocity).',
                badge: 'Passive Signal',
              },
              {
                step: '2. Simulate',
                desc: 'Synthetic patient models test alternative vocab layouts and dwell timings without fatiguing the user.',
                badge: 'AI Sandbox',
              },
              {
                step: '3. Human Review',
                desc: 'Specialist validates recommended adjustments or accepts automated real-time thresholds.',
                badge: 'Governance',
              },
              {
                step: '4. Deploy & Empower',
                desc: 'Updated assistive boards sync instantly across tablet, wheelchair switch, and cloud portal.',
                badge: 'Live Execution',
              },
            ].map((s, i) => (
              <div key={i} className="bg-clarvos-cream p-7 rounded-[28px] border border-clarvos-gray-300 relative group hover:border-clarvos-black transition-all duration-300">
                <span className="text-xs font-bold uppercase tracking-wider text-clarvos-gray-600 block mb-2">{s.badge}</span>
                <h4 className="text-xl font-extrabold mb-3">{s.step}</h4>
                <p className="text-sm text-clarvos-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Large Statement Pill */}
      <section className="py-24 px-6 sm:px-12 max-w-5xl mx-auto text-center">
        <div className="bg-clarvos-yellow p-12 sm:p-16 rounded-[40px] shadow-xl text-clarvos-black">
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight leading-snug mb-6">
            You stay in control. AbilityOS models and recommends — but every clinical decision can be reviewed, guided, and refined by you.
          </h2>
          <div className="flex justify-center">
            <ClarvosCTAButton
              text="Request a Demo"
              hoverText="Get Started"
              onClick={onRequestDemo}
              variant="dark"
              size="lg"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
